import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, Heart, ChatText } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ProductCard from "@/components/ProductCard";

const TABS = [
  { key: "orders", label: "Orders", icon: Package },
  { key: "saved", label: "Saved", icon: Heart },
  { key: "inquiries", label: "Inquiries", icon: ChatText },
];

export default function Account() {
  const { user } = useAuth();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [saved, setSaved] = useState([]);
  const [inquiries, setInquiries] = useState([]);

  useEffect(() => {
    api.get("/orders").then(({ data }) => setOrders(data)).catch(() => {});
    api.get("/account/saved").then(({ data }) => setSaved(data)).catch(() => {});
    api.get("/inquiries").then(({ data }) => setInquiries(data)).catch(() => {});
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-14" data-testid="account-page">
      <div className="flex items-center gap-4 mb-10">
        <div className="h-14 w-14 rounded-full bg-primary/20 grid place-items-center text-primary text-xl font-modent font-semibold">{(user?.name || "?")[0]}</div>
        <div>
          <h1 className="text-3xl font-modern font-light">{user?.name}</h1>
          <p className="text-muted-foreground text-sm">{user?.email}</p>
        </div>
      </div>

      <div className="flex gap-2 mb-8 border-b border-white/10">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 px-4 py-3 overline transition-colors border-b-2 -mb-px ${tab === t.key ? "text-primary border-primary" : "text-muted-foreground border-transparent hover:text-white"}`}
              data-testid={`account-tab-${t.key}`}>
              <Icon size={16} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "orders" && (
        <div className="space-y-4" data-testid="account-orders">
          {orders.length === 0 ? <p className="text-muted-foreground py-10">No orders yet. <Link to="/shop" className="text-primary">Start shopping →</Link></p> :
            orders.map((o) => (
              <div key={o.id} className="p-5 rounded-xl bg-card border border-white/5" data-testid={`order-${o.id}`}>
                <div className="flex justify-between items-center mb-3">
                  <span className="overline text-muted-foreground">{new Date(o.created_at).toLocaleString()}</span>
                  <span className="overline px-2.5 py-1 rounded-full bg-primary/15 text-primary">{o.status}</span>
                </div>
                <div className="space-y-1.5">
                  {o.items.map((it, i) => (
                    <div key={i} className="flex justify-between text-sm"><span className="text-muted-foreground">{it.name} × {it.quantity}</span><span>${it.subtotal.toLocaleString()}</span></div>
                  ))}
                </div>
                <div className="flex justify-between mt-3 pt-3 border-t border-white/10 font-modern"><span>Total</span><span className="text-primary">${o.total.toLocaleString()}</span></div>
              </div>
            ))}
        </div>
      )}

      {tab === "saved" && (
        saved.length === 0 ? <p className="text-muted-foreground py-10" data-testid="account-saved-empty">No saved items yet.</p> :
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="account-saved">
          {saved.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      )}

      {tab === "inquiries" && (
        <div className="space-y-4" data-testid="account-inquiries">
          {inquiries.length === 0 ? <p className="text-muted-foreground py-10">No inquiries submitted. <Link to="/contact" className="text-primary">Contact us →</Link></p> :
            inquiries.map((q) => (
              <div key={q.id} className="p-5 rounded-xl bg-card border border-white/5">
                <div className="flex justify-between items-center mb-2">
                  <span className="overline text-primary">{q.type}</span>
                  <span className="overline px-2.5 py-1 rounded-full bg-muted text-muted-foreground">{q.status}</span>
                </div>
                <p className="font-modern font-medium mb-1">{q.subject || "Inquiry"}</p>
                <p className="text-sm text-muted-foreground">{q.message}</p>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
